<template>
  <div class="app-navbar-actions">
   

    <LanguageSwitcher  class="app-navbar-actions__item hide-on-mobile" />

    <div
      v-if="!isAdminRole"
      class="app-navbar-actions__item notification-bell"
      @click="showNotifications = !showNotifications"
    >
      <div class="notification-bell__wrapper">
        <VaIcon :name="getBellIcon()" class="notification-bell__icon" />
        <span v-if="unreadCount > 0" class="notification-badge">{{ unreadCount > 99 ? '99+' : unreadCount }}</span>
      </div>
    </div>

    <VaDropdown v-if="!isAdminRole" v-model="showNotifications" placement="bottom-end" :offset="[0, 12]" class="notification-dropdown-wrapper">
      <template #anchor>
        <div></div>
      </template>

      <VaDropdownContent class="notification-dropdown">
        <div class="notification-header">
          <h3>{{ t('notifications.title') }}</h3>
          <VaButton
            v-if="isAdminRole"
            size="small"
            preset="plain"
            :disabled="unreadCount === 0 || isLoading"
            @click="markAllAsRead"
          >
            {{ t('notifications.markAllAsRead') }}
          </VaButton>
        </div>

        <div v-if="isLoading" class="notification-loading">
          <VaProgressCircle indeterminate size="2rem" />
          <p>{{ t('notifications.loading') }}</p>
        </div>

        <div v-else-if="notifications.length > 0" class="notification-list">
          <div
            v-for="notification in notifications"
            :key="notification.id"
            class="notification-item"
            :class="{ 'notification-item--unread': notification.status === 'non lu' }"
            @click="handleNotificationClick(notification)"
          >
            <div class="notification-icon" :class="`notification-icon--info`">
              <VaIcon :name="getNotificationIcon(notification)" />
            </div>
            <div class="notification-content">
              <p class="notification-title">{{ notification.title }}</p>
              <p class="notification-message" v-html="stripHtml(notification.content)"></p>
              <span class="notification-time">{{ formatTime(notification.publish_at) }}</span>
            </div>
            <div class="notification-actions">
              <VaButton 
                size="small" 
                preset="plain" 
                icon="visibility" 
                class="view-notification-btn"
                @click.stop="viewNotification(notification)"
              >
                <span class="view-btn-text">{{ t('notifications.view') }}</span>
              </VaButton>
            </div>
            <div v-if="notification.status === 'non lu'" class="notification-dot"></div>
            <div v-if="notification.force_confirm === 1 && !notification.confirmed" class="notification-confirm-badge">
              <VaIcon name="priority_high" size="small" />
            </div>
          </div>
        </div>

        <div v-else class="notification-empty">
          <VaIcon name="notifications_none" size="3rem" />
          <p>{{ t('notifications.noNotifications') }}</p>
        </div>

      </VaDropdownContent>
    </VaDropdown>

    <!-- Modal de visualisation -->
    <VaModal
      v-if="!isAdminRole"
      v-model="showViewModal"
      size="medium"
      hide-default-actions
      overlay-opacity="0.5"
      class="notification-modal-responsive"
    >
      <template #header>
        <div class="notification-view-modal-header">
          <div class="notification-view-modal-header__icon-wrapper">
            <VaIcon name="notifications" class="notification-view-modal-header__icon" />
          </div>
          <h3 class="notification-view-modal-header__title">{{ t('notifications.viewNotification') }}</h3>
        </div>
      </template>

      <div class="notification-view-modal-content">
        <h2 class="notification-view-modal-content__title">{{ selectedViewNotification?.title }}</h2>
        <div class="notification-view-modal-content__time">
          <VaIcon name="schedule" size="small" />
          <span>{{ formatTime(selectedViewNotification?.publish_at || '') }}</span>
        </div>

        <div class="notification-view-modal-content__divider"></div>

        <div class="notification-view-modal-content__body" v-html="selectedViewNotification?.content"></div>
      </div>

      <template #footer>
        <div class="notification-view-modal-footer">
          <VaButton preset="secondary" @click="closeViewModal" class="modal-btn"> {{ t('notifications.close') }} </VaButton>
        </div>
      </template>
    </VaModal>

    <!-- Overlay bloquant : notifications non lues à la connexion (une par une) -->
    <Teleport to="body">
      <Transition name="overlay-fade">
        <div
          v-if="!isAdminRole && showBlockingOverlay && currentBlockingNotification"
          class="agent-notification-blocker"
        >
          <div class="agent-notification-blocker__backdrop" />
          <div class="agent-notification-blocker__dialog">
            <!-- Progress bar at top -->
            <div class="agent-notification-blocker__top-progress">
              <div
                class="agent-notification-blocker__top-progress-fill"
                :style="{ width: `${((blockingIndex + 1) / blockingQueue.length) * 100}%` }"
              />
            </div>

            <!-- Header -->
            <div class="agent-notification-blocker__header">
              <div class="agent-notification-blocker__header-main">
                <div
                  class="agent-notification-blocker__icon"
                  :class="{ 'agent-notification-blocker__icon--urgent': currentBlockingNotification.force_confirm === 1 && !currentBlockingNotification.confirmed }"
                >
                  <VaIcon
                    :name="currentBlockingNotification.force_confirm === 1 && !currentBlockingNotification.confirmed ? 'notifications_active' : 'mark_email_unread'"
                    size="large"
                  />
                </div>
                <div class="agent-notification-blocker__header-info">
                  <span class="agent-notification-blocker__badge">
                    {{ blockingIndex + 1 }} / {{ blockingQueue.length }}
                  </span>
                  <h3 class="agent-notification-blocker__header-title">
                    {{ currentBlockingNotification.force_confirm === 1 && !currentBlockingNotification.confirmed
                      ? t('notifications.importantNotification')
                      : t('notifications.newNotification') }}
                  </h3>
                </div>
              </div>
            </div>

            <!-- Body -->
            <div class="agent-notification-blocker__body">
              <div class="agent-notification-blocker__meta">
                <VaIcon name="schedule" size="small" />
                <span>{{ formatTime(currentBlockingNotification.publish_at) }}</span>
              </div>

              <h2 class="agent-notification-blocker__title">{{ currentBlockingNotification.title }}</h2>

              <div class="agent-notification-blocker__content-card">
                <div class="agent-notification-blocker__content" v-html="currentBlockingNotification.content" />
              </div>

              <!-- Action required warning -->
              <div
                v-if="currentBlockingNotification.force_confirm === 1 && !currentBlockingNotification.confirmed"
                class="agent-notification-blocker__warning"
              >
                <VaIcon name="priority_high" class="agent-notification-blocker__warning-icon" />
                <div class="agent-notification-blocker__warning-text">
                  <strong>{{ t('notifications.actionRequired') }}</strong>
                  <span>{{ t('notifications.confirmToContinue') }}</span>
                </div>
              </div>
            </div>

            <!-- Footer -->
            <div class="agent-notification-blocker__footer">
              <VaButton
                :color="currentBlockingNotification.force_confirm === 1 && !currentBlockingNotification.confirmed ? 'warning' : 'primary'"
                :loading="isBlockingAction"
                size="large"
                class="agent-notification-blocker__action-btn"
                @click="handleBlockingAction"
              >
                <template #icon>
                  <VaIcon :name="currentBlockingNotification.force_confirm === 1 && !currentBlockingNotification.confirmed ? 'priority_high' : 'check'" />
                </template>
                {{ currentBlockingNotification.force_confirm === 1 && !currentBlockingNotification.confirmed
                  ? t('notifications.iUnderstand')
                  : t('notifications.markAsRead') }}
              </VaButton>

              <p class="agent-notification-blocker__helper">
                {{ t('notifications.readAllToContinue', { remaining: blockingQueue.length - blockingIndex }) }}
              </p>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>

    <!-- Modal de confirmation -->
    <VaModal
      v-if="!isAdminRole"
      v-model="showConfirmModal"
      size="medium"
      :before-close="handleModalClose"
      hide-default-actions
      overlay-opacity="0.5"
      class="notification-modal-responsive"
    >
      <template #header>
        <div class="notification-modal-header">
          <div class="notification-modal-header__icon-wrapper">
            <VaIcon name="notifications_active" class="notification-modal-header__icon" />
          </div>
          <h3 class="notification-modal-header__title">{{ t('notifications.importantNotification') }}</h3>
        </div>
      </template>

      <div class="notification-modal-content">
        <h2 class="notification-modal-content__title">{{ selectedNotification?.title }}</h2>
        <div class="notification-modal-content__time">
          <VaIcon name="schedule" size="small" />
          <span>{{ formatTime(selectedNotification?.publish_at || '') }}</span>
        </div>

        <div class="notification-modal-content__divider"></div>

        <div class="notification-modal-content__body" v-html="selectedNotification?.content"></div>

        <div class="notification-modal-content__alert">
          <div class="notification-modal-content__alert-icon">
            <VaIcon name="info" />
          </div>
          <div class="notification-modal-content__alert-text">
            <strong>{{ t('notifications.actionRequired') }}</strong>
            <p>{{ t('notifications.confirmReading') }}</p>
          </div>
        </div>
      </div>

      <template #footer>
        <div class="notification-modal-footer">
          <VaButton preset="secondary" :disabled="isConfirming" @click="closeModal" class="modal-btn"> {{ t('notifications.cancel') }} </VaButton>
          <VaButton :loading="isConfirming" icon-right="check" @click="confirmNotification" class="modal-btn"> {{ t('notifications.iUnderstand') }} </VaButton>
        </div>
      </template>
    </VaModal>

    <!-- <div v-if="!wsStore.isWebSocketSupported" class="websocket-support-message">WebSocket non supporté</div> -->
    <ThemeSwitcher class="app-navbar-actions__item theme-switcher hide-on-mobile" />
    <ProfileDropdown class="app-navbar-actions__item app-navbar-actions__item--profile profile-right" />
  </div>
</template>
<script lang="ts" setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import ProfileDropdown from './dropdowns/ProfileDropdown.vue'
import ThemeSwitcher from '../../../pages/settings/theme-switcher/ThemeSwitcher.vue'
import LanguageSwitcher from '../../../pages/settings/language-switcher/LanguageSwitcher.vue'
import { useWebSocketStore } from '../../../services/websocketServices/store'
import {
  announcementSocketService,
  type AnnouncementData,
} from '../../../services/websocketServices/announcementSocketService'
import socketService from '../../../services/websocketServices/socketInstance'
import axios from 'axios'
import api from '../../../services/api'
import { getActiveToken } from '../../../services/axios'
import { useAdminStore } from '../../../stores/admin'

const { t } = useI18n()

const wsStore = useWebSocketStore()
const adminStore = useAdminStore()

defineProps({
  isMobile: { type: Boolean, default: false },
})

interface Notification {
  id: number
  title: string
  content: string
  created_at: string
  publish_at: string
  status: 'lu' | 'non lu'
  confirmed: boolean
  force_confirm: 0 | 1
}

interface NotificationStats {
  data: Notification[]
  totalNotification: number
  read: number
  unread: number
  totalPage: number
  page: number
}

const showNotifications = ref(false)
const notifications = ref<Notification[]>([])
const isLoading = ref(false)
const unreadCount = ref(0)
let refreshInterval: NodeJS.Timeout | null = null
const scheduledTimers = new Map<number, NodeJS.Timeout>() // Minuteurs pour notifications programmées

// Variables pour la modal
const showConfirmModal = ref(false)
const selectedNotification = ref<Notification | null>(null)
const isConfirming = ref(false)

// Variables pour la modal de visualisation
const showViewModal = ref(false)
const selectedViewNotification = ref<Notification | null>(null)

// Overlay bloquant à la connexion (notifications une par une)
const showBlockingOverlay = ref(false)
const blockingQueue = ref<Notification[]>([])
const blockingIndex = ref(0)
const isBlockingAction = ref(false)

const currentBlockingNotification = computed(() => blockingQueue.value[blockingIndex.value] ?? null)

// Computed property pour vérifier si l'utilisateur est un admin
const isAdminRole = computed(() => {
  return adminStore.role === 'admin' || adminStore.role === 'super_admin'
})

const getNotifications = async (): Promise<NotificationStats | null> => {
  try {
    const token = getActiveToken()
    if (!token) {
      return null
    }

    const response = await axios.post(
      api.getNotifications(),
      {
        page: 1,
        pageSize: 10,
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    )

    // Filtrer les notifications selon l'heure
    const filteredData = filterNotificationsByTime(response.data.data)

    const filteredResponse = {
      ...response.data,
      data: filteredData,
      totalNotification: filteredData.length,
      unread: filteredData.filter((n) => n.status === 'non lu').length,
    }

    

    return filteredResponse
  } catch (error) {
    console.error('❌ Erreur dans getNotifications:', error)
    return null
  }
}

// Fonction pour filtrer les notifications selon l'heure
const filterNotificationsByTime = (notifications: Notification[]): Notification[] => {

  // TEMPORAIRE: Désactiver le filtrage pour diagnostiquer
  // Retourner toutes les notifications sans filtrage
  return notifications

  // Code original commenté pour diagnostic:
  /*
  const now = new Date()
  console.log("🕐 Heure actuelle:", now.toISOString())
  
  const filtered = notifications.filter(notification => {
    const publishTime = new Date(notification.publish_at)
    console.log(`📅 Notification "${notification.title}":`)
    console.log(`   Heure de publication: ${publishTime.toISOString()}`)
    
    // Si l'heure de publication est dans le futur -> ne pas afficher MAIS créer un minuteur
    if (publishTime > now) {
      console.log(`   ❌ Futur - NON AFFICHÉ pour l'instant`)
      // Créer un minuteur pour afficher automatiquement quand l'heure arrive
      scheduleNotificationTimer(notification, publishTime)
      return false
    }
    
    // Si l'heure de publication est passée ou égale à l'heure actuelle -> afficher
    console.log(`   ✅ Passé/Présent - AFFICHÉ`)
    return true
  })
  
  console.log(`📊 Résultat du filtrage: ${filtered.length}/${notifications.length} notifications affichées`)
  return filtered
  */
}

// Programmer un minuteur pour une notification future
const scheduleNotificationTimer = (notification: Notification, publishTime: Date) => {
  // Annuler le minuteur existant si déjà programmé
  if (scheduledTimers.has(notification.id)) {
    clearTimeout(scheduledTimers.get(notification.id))
  }

  const delay = publishTime.getTime() - Date.now()

  if (delay > 0) {

    const timer = setTimeout(() => {
      // Ajouter la notification à la liste
      notifications.value.unshift(notification)
      unreadCount.value = notifications.value.filter((n) => n.status === 'non lu').length
      addToBlockingQueue(notification)

      // Afficher une notification toast
      showNotificationToast(notification.title, t('notifications.newNotificationReceived'))

      // Nettoyer le minuteur
      scheduledTimers.delete(notification.id)

    }, delay)

    scheduledTimers.set(notification.id, timer)
  }
}

// Annuler tous les minuteurs
const clearAllScheduledTimers = () => {
  scheduledTimers.forEach((timer, id) => {
    clearTimeout(timer)
  })
  scheduledTimers.clear()
}

// Vérifier les nouvelles annonces programmées
const checkForNewScheduledAnnouncements = async () => {
  try {
    const token = getActiveToken()
    if (!token) return

    const response = await fetch(api.listAnnouncements(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        page: 1,
        pageSize: 50,
      }),
    })

    if (response.ok) {
      const data = await response.json()

      // Filtrer les annonces programmées non encore traitées
      const scheduledAnnouncements =
        data.data?.filter(
          (announcement: any) => !announcement.publish_now && new Date(announcement.publish_at) > new Date(),
        ) || []


      // Créer des minuteurs pour les nouvelles annonces programmées
      scheduledAnnouncements.forEach((announcement: any) => {
        const publishTime = new Date(announcement.publish_at)

        // Convertir l'annonce en notification
        const notification = convertAnnouncementToNotification({
          id: announcement.id,
          title: announcement.title,
          content: announcement.content,
          publish_at: announcement.publish_at,
          publish_now: announcement.publish_now,
          force_confirm: announcement.force_confirm || false,
          agent_ids: announcement.agent_ids || [],
        })

        // Créer un minuteur si l'heure est dans le futur
        if (publishTime > new Date()) {
          scheduleNotificationTimer(notification, publishTime)
        }
      })
    }
  } catch (error) {
    console.error('❌ Erreur lors de la vérification des annonces programmées:', error)
  }
}

const markAsRead = async (notificationId: number): Promise<boolean> => {
  try {
    const token = getActiveToken()
    if (!token) {
      return false
    }

    await axios.post(
      api.markNotificationAsRead(notificationId),
      {},
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    )
    return true
  } catch (error) {
    console.error('Erreur lors de la lecture:', error)
    return false
  }
}

const markAsConfirmed = async (notificationId: number): Promise<boolean> => {
  try {
    const token = getActiveToken()
    if (!token) {
      return false
    }

    await axios.post(
      api.markNotificationAsConfirmed(notificationId),
      {},
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    )
    return true
  } catch (error) {
    console.error('Erreur lors de la confirmation:', error)
    return false
  }
}

const markAllNotificationsAsRead = async (): Promise<boolean> => {
  try {
    const token = getActiveToken()
    if (!token) {
      return false
    }

    // Étape 1: Appeler l'URL principale pour marquer toutes les notifications comme lues
    const response = await axios.post(
      api.markAllNotificationsAsRead(),
      {},
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    )

    // Vérifier la réponse du serveur
    if (response.data && response.data.message === 'Toutes les notifications ont été marquées comme lues') {
      // Étape 2: Marquer chaque notification individuellement pour la synchronisation
      const unreadNotifications = notifications.value.filter((n) => n.status === 'non lu')

      // Paralléliser les appels individuels pour de meilleures performances
      const individualPromises = unreadNotifications.map((notification) =>
        axios
          .post(
            api.markNotificationAsRead(notification.id),
            {},
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            },
          )
          .catch((error) => {
            console.warn(`Erreur lors du marquage individuel de la notification ${notification.id}:`, error)
            return null // Ne pas échouer toute l'opération si une notification échoue
          }),
      )

      // Attendre que tous les appels individuels se terminent
      await Promise.all(individualPromises)

      return true
    } else {
      console.error('Réponse inattendue du serveur:', response.data)
      return false
    }
  } catch (error) {
    console.error('Erreur lors du marquage de toutes les notifications comme lues:', error)
    return false
  }
}

const convertAnnouncementToNotification = (announcement: AnnouncementData): Notification => {
  return {
    id: announcement.id,
    title: announcement.title,
    content: announcement.content,
    created_at: announcement.publish_at,
    publish_at: announcement.publish_at,
    status: 'non lu',
    confirmed: false,
    force_confirm: announcement.force_confirm ? 1 : 0,
  }
}

const showNotificationToast = (title: string, message: string) => {
  // Créer une notification toast simple
  if (typeof window !== 'undefined' && 'CustomEvent' in window) {
    const event = new CustomEvent('showToast', {
      detail: {
        title,
        message: t('notifications.newNotificationReceived'),
        type: 'success',
        duration: 5000,
      },
    })
    window.dispatchEvent(event)
  }
}

const sortNotificationsOldestFirst = (items: Notification[]) =>
  [...items].sort(
    (a, b) =>
      new Date(a.publish_at || a.created_at).getTime() - new Date(b.publish_at || b.created_at).getTime(),
  )

const addToBlockingQueue = (notification: Notification) => {
  if (notification.status !== 'non lu') return
  if (blockingQueue.value.some((n) => n.id === notification.id)) return

  if (showBlockingOverlay.value) {
    blockingQueue.value.push(notification)
    return
  }

  blockingQueue.value = [notification]
  blockingIndex.value = 0
  showBlockingOverlay.value = true
}

const initBlockingQueue = async () => {
  if (isAdminRole.value) return

  const unreadNotifications = await fetchAllUnreadNotifications()
  if (unreadNotifications.length === 0) return

  blockingQueue.value = sortNotificationsOldestFirst(unreadNotifications)
  blockingIndex.value = 0
  showBlockingOverlay.value = true
}

const advanceBlockingQueue = () => {
  if (blockingIndex.value < blockingQueue.value.length - 1) {
    blockingIndex.value++
    return
  }

  showBlockingOverlay.value = false
  blockingQueue.value = []
  blockingIndex.value = 0
}

const handleBlockingAction = async () => {
  const notification = currentBlockingNotification.value
  if (!notification || isBlockingAction.value) return

  isBlockingAction.value = true

  try {
    if (notification.force_confirm === 1 && !notification.confirmed) {
      const confirmSuccess = await markAsConfirmed(notification.id)
      if (!confirmSuccess) {
        alert(t('notifications.confirmError'))
        return
      }
      notification.confirmed = true
    }

    const readSuccess = await markAsRead(notification.id)
    if (!readSuccess) {
      alert(t('notifications.readError'))
      return
    }

    notification.status = 'lu'

    const listItem = notifications.value.find((n) => n.id === notification.id)
    if (listItem) {
      listItem.status = 'lu'
      if (notification.confirmed) {
        listItem.confirmed = true
      }
    }

    unreadCount.value = Math.max(0, unreadCount.value - 1)
    advanceBlockingQueue()
  } finally {
    isBlockingAction.value = false
  }
}

const fetchAllUnreadNotifications = async (): Promise<Notification[]> => {
  try {
    const token = getActiveToken()
    if (!token) return []

    const unreadNotifications: Notification[] = []
    let page = 1
    let totalPages = 1

    while (page <= totalPages) {
      const response = await axios.post(
        api.getNotifications(),
        { page, pageSize: 50 },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      )

      const filteredData = filterNotificationsByTime(response.data.data)
      unreadNotifications.push(...filteredData.filter((n: Notification) => n.status === 'non lu'))
      totalPages = response.data.totalPage || 1
      page++
    }

    return unreadNotifications
  } catch (error) {
    console.error('Erreur lors du chargement des notifications non lues:', error)
    return notifications.value.filter((n) => n.status === 'non lu')
  }
}

const addNewNotificationFromWebSocket = (announcement: AnnouncementData) => {

  const newNotification = convertAnnouncementToNotification(announcement)

  // Vérifier si la notification existe déjà
  const existingNotification = notifications.value.find((n) => n.id === newNotification.id)
  if (existingNotification) {
    if (existingNotification.status === 'non lu') {
      addToBlockingQueue(existingNotification)
    }
    return
  }

  // Ajouter la notification au début de la liste
  notifications.value.unshift(newNotification)

  // Mettre à jour le compteur de notifications non lues
  unreadCount.value = notifications.value.filter((n) => n.status === 'non lu').length

  // Limiter à 50 notifications pour éviter les surcharges
  if (notifications.value.length > 50) {
    notifications.value = notifications.value.slice(0, 50)
  }

  addToBlockingQueue(newNotification)

  // Afficher une notification toast visuelle
  showNotificationToast(newNotification.title, t('notifications.newNotificationReceived'))
}

const loadNotifications = async (showLoader = true, preserveLocalNotifications = false) => {
  if (showLoader) {
    isLoading.value = true
  }

  try {
    const response = await getNotifications()

    if (response) {
      if (preserveLocalNotifications && notifications.value.length > 0) {
        const serverNotificationIds = new Set(response.data.map((n) => n.id))
        const localNotifications = notifications.value.filter((n) => !serverNotificationIds.has(n.id))
        notifications.value = [...localNotifications, ...response.data]
        unreadCount.value = notifications.value.filter((n) => n.status === 'non lu').length
      } else {
        notifications.value = response.data
        unreadCount.value = response.unread
      }
    }
  } catch (error) {
  } finally {
    if (showLoader) {
      isLoading.value = false
    }
  }
}

const toggleNotifications = async () => {
  showNotifications.value = !showNotifications.value

  if (showNotifications.value) {
    await loadNotifications()
  }
}

const markAllAsRead = async () => {
  if (unreadCount.value === 0) return

  const success = await markAllNotificationsAsRead()

  if (success) {
    notifications.value.forEach((n) => {
      if (n.status === 'non lu') {
        n.status = 'lu'
      }
    })
    unreadCount.value = 0
  }
}

const handleNotificationClick = async (notification: Notification) => {
  // Si la notification nécessite une confirmation et n'est pas encore confirmée
  if (notification.force_confirm === 1 && !notification.confirmed) {
    selectedNotification.value = notification
    showConfirmModal.value = true
    showNotifications.value = false
  } else {
    // Sinon, marquer simplement comme lu
    if (notification.status === 'non lu') {
      const success = await markAsRead(notification.id)

      if (success) {
        notification.status = 'lu'
        unreadCount.value = Math.max(0, unreadCount.value - 1)
      }
    }
  }
}

const confirmNotification = async () => {
  if (!selectedNotification.value) return

  isConfirming.value = true

  try {
    // Marquer d'abord comme confirmé
    const confirmSuccess = await markAsConfirmed(selectedNotification.value.id)

    if (confirmSuccess) {
      // Ensuite marquer comme lu
      const readSuccess = await markAsRead(selectedNotification.value.id)

      // Mettre à jour la notification dans la liste
      const notif = notifications.value.find((n) => n.id === selectedNotification.value!.id)
      if (notif) {
        notif.confirmed = true

        // Si c'était non lu, mettre à jour le compteur
        if (notif.status === 'non lu') {
          notif.status = 'lu'
          unreadCount.value = Math.max(0, unreadCount.value - 1)
        }
      }

      // Fermer la modal
      showConfirmModal.value = false
      selectedNotification.value = null
    } else {
      alert('Erreur lors de la confirmation de la notification')
    }
  } finally {
    isConfirming.value = false
  }
}

const closeModal = () => {
  showConfirmModal.value = false
  selectedNotification.value = null
}

const handleModalClose = () => {
  if (!isConfirming.value) {
    closeModal()
    return true
  }
  return false
}

// Fonction pour visualiser une notification
const viewNotification = (notification: Notification) => {
  selectedViewNotification.value = notification
  showViewModal.value = true
  
  // Marquer comme lu si non lu
  if (notification.status === 'non lu') {
    markAsRead(notification.id).then((success) => {
      if (success) {
        notification.status = 'lu'
        unreadCount.value = Math.max(0, unreadCount.value - 1)
      }
    })
  }
}

// Fonction pour fermer la modal de visualisation
const closeViewModal = () => {
  showViewModal.value = false
  selectedViewNotification.value = null
}

const getNotificationIcon = (notification: Notification) => {
  // Icône différente selon le rôle de l'admin
  if (adminStore.role === 'admin' || adminStore.role === 'super_admin') {
    return 'admin_panel_settings' // Icône pour les admins
  } else if (adminStore.role === 'moderator') {
    return 'shield_person' // Icône pour les modérateurs
  } else {
    return 'notifications' // Icône par défaut
  }
}

const getBellIcon = () => {
  // Icône de la cloche principale selon le rôle de l'admin
  if (adminStore.role === 'admin' || adminStore.role === 'super_admin') {
    return 'admin_panel_settings' // Icône admin pour la cloche
  } else if (adminStore.role === 'moderator') {
    return 'shield_person' // Icône modérateur pour la cloche
  } else {
    return 'notifications' // Icône par défaut
  }
}

const stripHtml = (html: string): string => {
  if (!html) return ''

  const tmp = document.createElement('DIV')
  tmp.innerHTML = html
  return tmp.textContent || tmp.innerText || ''
}

const formatTime = (dateString: string): string => {
  const date = new Date(dateString)
  const diff = Date.now() - date.getTime()
  const minutes = Math.floor(diff / 60000)
  const hours = Math.floor(diff / 3600000)
  const days = Math.floor(diff / 86400000)

  if (minutes < 1) return t('notifications.atTheInstant')
  if (minutes < 60) return t('notifications.minutesAgo').replace('{count}', minutes.toString())
  if (hours < 24) return t('notifications.hoursAgo').replace('{count}', hours.toString())
  if (days === 1) return t('notifications.yesterday')
  if (days < 7) return t('notifications.daysAgo').replace('{count}', days.toString())

  return date.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: 'short',
  })
}

onMounted(async () => {

  // Initialiser le store admin pour avoir le rôle correct
  await adminStore.getAdminAuthenticate()

  await getNotifications()

  if (!socketService.isConnected()) {
    socketService.connect()
  } else {
  }

  // Écouter les nouvelles annonces (immédiates ou programmées)
  announcementSocketService.onAnnouncementCreated((announcement: AnnouncementData) => {
    

    // Ne pas afficher les notifications programmées tant qu'elles ne sont pas publiées
    if (announcement.publish_now) {
      if (!isAdminRole.value) {
        addNewNotificationFromWebSocket(announcement)
      } else {
      } 
    } else {
    }
  })

  // Écouter les annonces programmées qui sont maintenant publiées
  announcementSocketService.onAnnouncementPublished((announcement: AnnouncementData) => {
   
    if (!isAdminRole.value) {
      addNewNotificationFromWebSocket(announcement)
    } else {
    }
  })

  // Écouter les événements spécifiques aux agents
  socketService.onMessage('agent_announcement', (data: AnnouncementData) => {
    console.log(' Événement agent_announcement reçu:', data)
    if (!isAdminRole.value) {
      addNewNotificationFromWebSocket(data)
    }
  })

  socketService.onMessage('agent_announcement_published', (data: AnnouncementData) => {
    if (adminStore.role !== 'admin' && adminStore.role !== 'super_admin') {
      addNewNotificationFromWebSocket(data)
    }
  })

  // Initialiser les notifications pour les agents (non-admins)

  // Charger les notifications si l'utilisateur n'est pas admin/super_admin
  // Donc les agents, moderators, etc. verront les notifications
  if (adminStore.role !== 'admin' && adminStore.role !== 'super_admin') {
    await loadNotifications()
    await initBlockingQueue()

    // Vérifier immédiatement les annonces programmées
    await checkForNewScheduledAnnouncements()

    // Démarrer la synchronisation automatique toutes les 30 secondes
    refreshInterval = setInterval(async () => {
      await loadNotifications(false, true) // preserveLocalNotifications = true

      // Vérifier aussi les nouvelles annonces programmées
      await checkForNewScheduledAnnouncements()
    }, 30000)

    window.addEventListener('newAnnouncement', (event: Event) => {
      const customEvent = event as CustomEvent
      const existingNotification = notifications.value.find((n) => n.id === customEvent.detail.id)
      if (existingNotification) {
        return
      }

      const newNotification: Notification = {
        id: customEvent.detail.id,
        title: customEvent.detail.title,
        content: customEvent.detail.content,
        created_at: customEvent.detail.created_at,
        publish_at: customEvent.detail.created_at,
        status: customEvent.detail.read ? 'lu' : 'non lu',
        confirmed: customEvent.detail.confirmed || false,
        force_confirm: customEvent.detail.force_confirm || 0,
      }

      notifications.value.unshift(newNotification)

      if (!customEvent.detail.read) {
        unreadCount.value++
        addToBlockingQueue(newNotification)
      }

      if (notifications.value.length > 50) {
        notifications.value = notifications.value.slice(0, 50)
      }
    })
  }
})

watch(showBlockingOverlay, (isBlocking) => {
  if (typeof document === 'undefined') return
  document.body.style.overflow = isBlocking ? 'hidden' : ''
})

onUnmounted(() => {
  if (refreshInterval) {
    clearInterval(refreshInterval)
  }

  document.body.style.overflow = ''

  // Nettoyer tous les minuteurs d'annonces programmées
  announcementSocketService.clearAllTimers()

  // Nettoyer tous les minuteurs de notifications programmées
  clearAllScheduledTimers()
})
</script>

<style lang="scss">
// ============================================
// NAVBAR ACTIONS — DESIGN ÉPURÉ RESPONSIVE
// ============================================
.hide-on-mobile {
  @media screen and (max-width: 640px) {
    display: none !important;
  }
}
.app-navbar-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.5rem;
  flex-wrap: nowrap;
  min-width: 0;
  overflow: visible;
   padding-right: 0;
  .va-dropdown__anchor {
    color: var(--va-primary);
    fill: var(--va-primary);
  }

  &__item {
    padding: 0;
    margin-left: 0;
    margin-right: 0;
    flex-shrink: 0;
    display: flex;
    align-items: center;

    svg {
      height: 20px;
    }

    &--profile {
      display: flex;
      justify-content: flex-end;
    }

    .va-dropdown-content {
      background-color: var(--va-white);
    }

    // LanguageSwitcher & ThemeSwitcher — boutons compacts
    &.language-switcher,
    &.theme-switcher {
      .va-button {
        width: 36px;
        height: 36px;
        min-width: 36px;
        padding: 0;
        border-radius: 10px;
        display: flex;
        align-items: center;
        justify-content: center;
        
        .va-icon {
          font-size: 18px;
        }
      }
    }
  }

  // ============================================
  // NOTIFICATION BELL — DESIGN ÉPURÉ
  // ============================================
  .notification-bell {
    position: relative;
    cursor: pointer;
    width: 40px;
    height: 40px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
    margin-left: 0;
    margin-right: 0;

    &:hover {
      background-color: var(--va-background-element);
      
      .notification-bell__icon {
        animation: bellRing 0.5s ease;
      }
    }

    &:active {
      transform: scale(0.95);
    }

    &__wrapper {
      position: relative;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    &__icon {
      font-size: 22px !important;
      color: var(--va-primary);
      display: block;
    }
  }

  .notification-badge {
    position: absolute;
    top: -6px;
    right: -6px;
    background: linear-gradient(135deg, var(--va-danger) 0%, #ff1744 100%);
    color: white;
    border-radius: 10px;
    min-width: 18px;
    height: 18px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 10px;
    font-weight: 700;
    padding: 0 5px;
    box-shadow: 0 2px 6px rgba(244, 67, 54, 0.4);
    border: 2px solid var(--va-background-primary);
    animation: pulse 2s infinite;
    white-space: nowrap;
  }

  // ============================================
  // RESPONSIVE — MOBILE OPTIMISÉ
  // ============================================
  
  @media screen and (max-width: 768px) {
    gap: 0.375rem;
    
    &__item {
      // Tous les éléments plus compacts
      .va-button {
        width: 36px;
        height: 36px;
        min-width: 36px;
        padding: 0;
        
        .va-icon {
          font-size: 18px;
        }
      }
    }
    
    .notification-bell {
      width: 36px;
      height: 36px;
      
      &__icon {
        font-size: 20px !important;
      }
    }
    
    .notification-badge {
      top: -5px;
      right: -5px;
      min-width: 16px;
      height: 16px;
      font-size: 9px;
      padding: 0 4px;
    }
  }

  @media screen and (max-width: 480px) {
    gap: 0.25rem;
    
    &__item {
      .va-button {
        width: 34px;
        height: 34px;
        min-width: 34px;
        
        .va-icon {
          font-size: 16px;
        }
      }
      
      // Masquer le texte du profil, garder juste l'avatar
      &--profile {
        .profile-dropdown__name {
          display: none;
        }
      }
    }
    
    .notification-bell {
      width: 34px;
      height: 34px;
      
      &__icon {
        font-size: 18px !important;
      }
    }
    
    .notification-badge {
      top: -4px;
      right: -4px;
      min-width: 15px;
      height: 15px;
      font-size: 8px;
      padding: 0 3px;
    }
  }
  
  @media screen and (max-width: 360px) {
    gap: 0.125rem;
    
    &__item {
      .va-button {
        width: 32px;
        height: 32px;
        min-width: 32px;
        
        .va-icon {
          font-size: 15px;
        }
      }
    }
    
    .notification-bell {
      width: 32px;
      height: 32px;
      
      &__icon {
        font-size: 16px !important;
      }
    }
  }
}

// ============================================
// ANIMATIONS
// ============================================

@keyframes bellRing {
  0%, 100% { transform: rotate(0deg); }
  10%, 30% { transform: rotate(-10deg); }
  20%, 40% { transform: rotate(10deg); }
  50% { transform: rotate(0deg); }
}

@keyframes pulse {
  0%, 100% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.1); opacity: 0.9; }
}

// ============================================
// NOTIFICATION DROPDOWN — INTACT
// ============================================

.notification-dropdown-wrapper {
  width: 100%;
}

.notification-dropdown {
  width: 400px;
  max-height: 580px;
  background-color: var(--va-background-element);
  border-radius: 16px;
  box-shadow:
    0 8px 32px rgba(0, 0, 0, 0.12),
    0 2px 8px rgba(0, 0, 0, 0.04);
  overflow: hidden;
  border: 1px solid rgba(var(--va-background-border-rgb), 0.5);

  @media screen and (max-width: 480px) {
    width: 92vw;
    max-width: 400px;
    max-height: 85vh;
    border-radius: 12px;
  }
  
  @media screen and (max-width: 360px) {
    width: 95vw;
    max-height: 80vh;
  }

  .notification-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1.25rem 1.5rem;
    background: linear-gradient(
      to bottom,
      var(--va-background-element) 0%,
      rgba(var(--va-background-border-rgb), 0.2) 100%
    );
    border-bottom: 1px solid var(--va-background-border);
    gap: 0.75rem;

    h3 {
      margin: 0;
      font-size: 1.125rem;
      font-weight: 700;
      color: var(--va-on-background-primary);
      letter-spacing: -0.01em;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .va-button {
      font-size: 0.8125rem;
      color: var(--va-primary);
      font-weight: 600;
      padding: 0.375rem 0.75rem;
      border-radius: 6px;
      transition: all 0.2s ease;
      white-space: nowrap;
      flex-shrink: 0;

      &:hover:not(:disabled) {
        background-color: rgba(var(--va-primary-rgb), 0.1);
      }

      &:disabled {
        opacity: 0.4;
        cursor: not-allowed;
      }
      
      @media screen and (max-width: 480px) {
        font-size: 0.75rem;
        padding: 0.3rem 0.6rem;
      }
    }
    
    @media screen and (max-width: 480px) {
      padding: 1rem 1.25rem;
      
      h3 {
        font-size: 1rem;
      }
    }
  }

  .notification-loading {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 3rem 1rem;
    gap: 1rem;

    p {
      margin: 0;
      color: var(--va-on-background-secondary);
      font-size: 0.9rem;
    }
  }

  .notification-list {
    max-height: 480px;
    overflow-y: auto;

    @media screen and (max-width: 480px) {
      max-height: calc(85vh - 120px);
    }
    
    @media screen and (max-width: 360px) {
      max-height: calc(80vh - 120px);
    }

    &::-webkit-scrollbar {
      width: 6px;
    }

    &::-webkit-scrollbar-track {
      background: transparent;
      margin: 4px 0;
    }

    &::-webkit-scrollbar-thumb {
      background-color: var(--va-background-border);
      border-radius: 3px;

      &:hover {
        background-color: rgba(var(--va-primary-rgb), 0.3);
      }
    }
  }

  .notification-item {
    display: flex;
    align-items: flex-start;
    padding: 1rem 1.25rem;
    border-bottom: 1px solid rgba(var(--va-background-border-rgb), 0.6);
    cursor: pointer;
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
    position: relative;
    background: var(--va-background-element);
    gap: 0.75rem;

    &::before {
      content: '';
      position: absolute;
      left: 0;
      top: 0;
      bottom: 0;
      width: 3px;
      background: transparent;
      transition: background 0.2s ease;
    }

    &:hover {
      background-color: rgba(var(--va-background-border-rgb), 0.4);

      &::before {
        background: var(--va-primary);
      }
    }

    &--unread {
      background-color: rgba(var(--va-primary-rgb), 0.04);

      &::before {
        background: rgba(var(--va-primary-rgb), 0.3);
      }

      &:hover {
        background-color: rgba(var(--va-primary-rgb), 0.08);

        &::before {
          background: var(--va-primary);
        }
      }
    }

    &:last-child {
      border-bottom: none;
    }
    
    @media screen and (max-width: 480px) {
      padding: 0.875rem 1rem;
    }
    
    @media screen and (max-width: 360px) {
      padding: 0.75rem 0.875rem;
    }
  }

  .notification-actions {
    display: flex;
    align-items: center;
    margin-left: auto;
    flex-shrink: 0;
    
    @media screen and (max-width: 480px) {
      margin-left: 0.5rem;
    }

    .view-notification-btn {
      font-size: 0.75rem;
      padding: 0.25rem 0.5rem;
      min-height: 28px;
      color: var(--va-primary);
      opacity: 0.7;
      transition: all 0.2s ease;
      white-space: nowrap;

      &:hover {
        opacity: 1;
        background-color: rgba(var(--va-primary-rgb), 0.1);
        transform: scale(1.05);
      }

      .va-icon {
        font-size: 0.875rem !important;
      }
      
      .view-btn-text {
        @media screen and (max-width: 360px) {
          display: none;
        }
      }
      
      @media screen and (max-width: 480px) {
        font-size: 0.7rem;
        padding: 0.2rem 0.4rem;
        min-height: 24px;
      }
    }
  }

  .notification-icon {
    width: 44px;
    height: 44px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-right: 1rem;
    flex-shrink: 0;
    font-size: 1.375rem;
    transition: transform 0.2s ease;

    &--success {
      background: linear-gradient(135deg, rgba(var(--va-success-rgb), 0.15) 0%, rgba(var(--va-success-rgb), 0.08) 100%);
      color: var(--va-success);
    }

    &--warning {
      background: linear-gradient(135deg, rgba(var(--va-warning-rgb), 0.15) 0%, rgba(var(--va-warning-rgb), 0.08) 100%);
      color: var(--va-warning);
    }

    &--error {
      background: linear-gradient(135deg, rgba(var(--va-danger-rgb), 0.15) 0%, rgba(var(--va-danger-rgb), 0.08) 100%);
      color: var(--va-danger);
    }

    &--info {
      background: linear-gradient(135deg, rgba(var(--va-info-rgb), 0.15) 0%, rgba(var(--va-info-rgb), 0.08) 100%);
      color: var(--va-info);
    }
    
    @media screen and (max-width: 480px) {
      width: 36px;
      height: 36px;
      margin-right: 0.75rem;
      font-size: 1.125rem;
    }
    
    @media screen and (max-width: 360px) {
      width: 32px;
      height: 32px;
      font-size: 1rem;
    }
  }

  .notification-item:hover .notification-icon {
    transform: scale(1.05);
  }

  .notification-content {
    flex: 1;
    min-width: 0;

    .notification-title {
      margin: 0 0 0.375rem 0;
      font-weight: 600;
      font-size: 0.9375rem;
      color: var(--va-on-background-primary);
      line-height: 1.4;
      letter-spacing: -0.01em;
      overflow: hidden;
      text-overflow: ellipsis;
      display: -webkit-box;
      -webkit-line-clamp: 1;
      -webkit-box-orient: vertical;
      
      @media screen and (max-width: 480px) {
        font-size: 0.875rem;
        margin-bottom: 0.25rem;
      }
    }

    .notification-message {
      margin: 0 0 0.5rem 0;
      font-size: 0.8125rem;
      color: var(--va-on-background-secondary);
      line-height: 1.5;
      overflow: hidden;
      text-overflow: ellipsis;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      
      @media screen and (max-width: 480px) {
        font-size: 0.75rem;
        margin-bottom: 0.375rem;
        -webkit-line-clamp: 2;
      }
    }

    .notification-time {
      font-size: 0.75rem;
      color: var(--va-on-background-secondary);
      opacity: 0.65;
      font-weight: 500;
      letter-spacing: 0.01em;
      white-space: nowrap;
      
      @media screen and (max-width: 480px) {
        font-size: 0.7rem;
      }
    }
  }

  .notification-dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background-color: var(--va-primary);
    position: absolute;
    right: 1.25rem;
    top: 1.5rem;
    box-shadow: 0 0 0 3px rgba(var(--va-primary-rgb), 0.2);
    animation: pulse-dot 2s ease-in-out infinite;
    
    @media screen and (max-width: 480px) {
      width: 8px;
      height: 8px;
      right: 1rem;
      top: 1.25rem;
    }
  }

  @keyframes pulse-dot {
    0%, 100% { transform: scale(1); opacity: 1; }
    50% { transform: scale(1.1); opacity: 0.8; }
  }

  .notification-confirm-badge {
    position: absolute;
    right: 1rem;
    top: 1rem;
    width: 24px;
    height: 24px;
    border-radius: 6px;
    background: linear-gradient(135deg, var(--va-warning) 0%, #ff9800 100%);
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.875rem;
    box-shadow: 0 2px 8px rgba(255, 152, 0, 0.3);
    animation: pulse-badge 2s ease-in-out infinite;
    
    @media screen and (max-width: 480px) {
      width: 20px;
      height: 20px;
      font-size: 0.75rem;
      right: 0.75rem;
      top: 0.875rem;
    }
  }

  @keyframes pulse-badge {
    0%, 100% { transform: scale(1); }
    50% { transform: scale(1.05); }
  }

  .notification-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 3.5rem 1rem;
    color: var(--va-on-background-secondary);

    .va-icon {
      margin-bottom: 1rem;
      opacity: 0.4;
    }

    p {
      margin: 0;
      font-size: 0.95rem;
      font-weight: 500;
      text-align: center;
    }
    
    @media screen and (max-width: 480px) {
      padding: 2.5rem 1rem;
      
      p {
        font-size: 0.875rem;
      }
    }
  }
}

// ============================================
// MODALS NOTIFICATIONS — INTACTS
// ============================================

.notification-modal-responsive {
  :deep(.va-modal__dialog) {
    max-width: 90vw !important;
    width: 100% !important;
    
    @media screen and (max-width: 480px) {
      max-width: 95vw !important;
      margin: 0.5rem;
    }
  }
}

.notification-modal-header {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;

  &__icon-wrapper {
    width: 48px;
    height: 48px;
    border-radius: 12px;
    background: linear-gradient(135deg, rgba(var(--va-primary-rgb), 0.15) 0%, rgba(var(--va-primary-rgb), 0.05) 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    
    @media screen and (max-width: 480px) {
      width: 40px;
      height: 40px;
    }
  }

  &__icon {
    color: var(--va-primary);
    font-size: 1.75rem;
    
    @media screen and (max-width: 480px) {
      font-size: 1.5rem;
    }
  }

  &__title {
    margin: 0;
    font-size: 1.25rem;
    font-weight: 700;
    color: var(--va-on-background-primary);
    letter-spacing: -0.02em;
    
    @media screen and (max-width: 480px) {
      font-size: 1.125rem;
    }
  }
}

.notification-modal-content {
  padding: 0;

  &__title {
    margin: 0 0 1rem 0;
    font-size: 1.375rem;
    font-weight: 700;
    color: var(--va-on-background-primary);
    line-height: 1.3;
    letter-spacing: -0.02em;
    word-wrap: break-word;
    
    @media screen and (max-width: 480px) {
      font-size: 1.125rem;
    }
  }

  &__time {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    color: var(--va-on-background-secondary);
    font-size: 0.9375rem;
    margin-bottom: 1.5rem;
    padding: 0.625rem 1rem;
    background-color: rgba(var(--va-background-border-rgb), 0.3);
    border-radius: 8px;
    width: fit-content;
    max-width: 100%;
    
    @media screen and (max-width: 480px) {
      font-size: 0.8125rem;
      padding: 0.5rem 0.875rem;
    }
  }

  &__divider {
    height: 1px;
    background: linear-gradient(to right, transparent, var(--va-background-border), transparent);
    margin: 1.5rem 0;
  }

  &__body {
    color: var(--va-on-background-primary);
    line-height: 1.7;
    font-size: 1rem;
    max-height: 400px;
    overflow-y: auto;
    padding-right: 0.5rem;
    margin-bottom: 1.5rem;
    word-wrap: break-word;

    &::-webkit-scrollbar {
      width: 6px;
    }

    &::-webkit-scrollbar-track {
      background: transparent;
    }

    &::-webkit-scrollbar-thumb {
      background-color: var(--va-background-border);
      border-radius: 3px;

      &:hover {
        background-color: rgba(var(--va-primary-rgb), 0.3);
      }
    }

    p {
      margin-bottom: 1rem;

      &:last-child {
        margin-bottom: 0;
      }
    }

    ul,
    ol {
      margin-bottom: 1rem;
      padding-left: 1.5rem;
    }

    strong {
      font-weight: 600;
      color: var(--va-on-background-primary);
    }
    
    @media screen and (max-width: 480px) {
      font-size: 0.875rem;
      max-height: 300px;
    }
  }

  &__alert {
    display: flex;
    align-items: flex-start;
    gap: 1rem;
    padding: 1.25rem;
    background: linear-gradient(135deg, rgba(var(--va-primary-rgb), 0.08) 0%, rgba(var(--va-primary-rgb), 0.03) 100%);
    border: 1px solid rgba(var(--va-primary-rgb), 0.2);
    border-radius: 12px;
    flex-wrap: wrap;

    &-icon {
      width: 40px;
      height: 40px;
      border-radius: 10px;
      background-color: var(--va-primary);
      color: white;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      font-size: 1.25rem;
      
      @media screen and (max-width: 480px) {
        width: 32px;
        height: 32px;
        font-size: 1rem;
      }
    }

    &-text {
      flex: 1;
      min-width: 0;

      strong {
        display: block;
        font-size: 0.9375rem;
        font-weight: 700;
        color: var(--va-on-background-primary);
        margin-bottom: 0.25rem;
        letter-spacing: -0.01em;
        
        @media screen and (max-width: 480px) {
          font-size: 0.875rem;
        }
      }

      p {
        margin: 0;
        font-size: 0.875rem;
        color: var(--va-on-background-secondary);
        line-height: 1.5;
        
        @media screen and (max-width: 480px) {
          font-size: 0.8125rem;
        }
      }
    }
    
    @media screen and (max-width: 480px) {
      padding: 1rem;
      gap: 0.75rem;
    }
  }
}

.notification-modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  flex-wrap: wrap;

  .va-button,
  .modal-btn {
    min-width: 120px;
    font-weight: 600;
    
    @media screen and (max-width: 480px) {
      min-width: 100px;
      flex: 1;
    }
    
    @media screen and (max-width: 360px) {
      width: 100%;
      flex: unset;
    }
  }
  
  @media screen and (max-width: 360px) {
    flex-direction: column-reverse;
  }
}

// ============================================
// MODAL DE VISUALISATION — INTACT
// ============================================

.notification-view-modal-header {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;

  &__icon-wrapper {
    width: 48px;
    height: 48px;
    border-radius: 12px;
    background: linear-gradient(135deg, rgba(var(--va-info-rgb), 0.15) 0%, rgba(var(--va-info-rgb), 0.05) 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    
    @media screen and (max-width: 480px) {
      width: 40px;
      height: 40px;
    }
  }

  &__icon {
    color: var(--va-info);
    font-size: 1.75rem;
    
    @media screen and (max-width: 480px) {
      font-size: 1.5rem;
    }
  }

  &__title {
    margin: 0;
    font-size: 1.25rem;
    font-weight: 700;
    color: var(--va-on-background-primary);
    letter-spacing: -0.02em;
    
    @media screen and (max-width: 480px) {
      font-size: 1.125rem;
    }
  }
}

.notification-view-modal-content {
  padding: 0;

  &__title {
    margin: 0 0 1rem 0;
    font-size: 1.375rem;
    font-weight: 700;
    color: var(--va-on-background-primary);
    line-height: 1.3;
    letter-spacing: -0.02em;
    word-wrap: break-word;
    
    @media screen and (max-width: 480px) {
      font-size: 1.125rem;
    }
  }

  &__time {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    color: var(--va-on-background-secondary);
    font-size: 0.9375rem;
    margin-bottom: 1.5rem;
    padding: 0.625rem 1rem;
    background-color: rgba(var(--va-background-border-rgb), 0.3);
    border-radius: 8px;
    width: fit-content;
    max-width: 100%;
    
    @media screen and (max-width: 480px) {
      font-size: 0.8125rem;
      padding: 0.5rem 0.875rem;
    }
  }

  &__divider {
    height: 1px;
    background: linear-gradient(to right, transparent, var(--va-background-border), transparent);
    margin: 1.5rem 0;
  }

  &__body {
    color: var(--va-on-background-primary);
    line-height: 1.7;
    font-size: 1rem;
    max-height: 400px;
    overflow-y: auto;
    padding-right: 0.5rem;
    margin-bottom: 1.5rem;
    word-wrap: break-word;

    &::-webkit-scrollbar {
      width: 6px;
    }

    &::-webkit-scrollbar-track {
      background: transparent;
    }

    &::-webkit-scrollbar-thumb {
      background-color: var(--va-background-border);
      border-radius: 3px;

      &:hover {
        background-color: rgba(var(--va-primary-rgb), 0.3);
      }
    }

    p {
      margin-bottom: 1rem;

      &:last-child {
        margin-bottom: 0;
      }
    }

    ul,
    ol {
      margin-bottom: 1rem;
      padding-left: 1.5rem;
    }

    strong {
      font-weight: 600;
      color: var(--va-on-background-primary);
    }
    
    @media screen and (max-width: 480px) {
      font-size: 0.875rem;
      max-height: 300px;
    }
  }
}

.notification-view-modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;

  .va-button,
  .modal-btn {
    min-width: 120px;
    font-weight: 600;
    
    @media screen and (max-width: 480px) {
      min-width: 100px;
      width: 100%;
    }
  }
}

// ============================================
// OVERLAY BLOQUANT — CONNEXION AGENT (MODERN)
// ============================================

.agent-notification-blocker {
  position: fixed;
  inset: 0;
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;

  &__backdrop {
    position: absolute;
    inset: 0;
    background: rgba(15, 23, 42, 0.75);
    backdrop-filter: blur(8px);
  }

  &__dialog {
    position: relative;
    z-index: 1;
    width: min(560px, 100%);
    max-height: 85vh;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    background: var(--va-background-primary);
    border-radius: 20px;
    box-shadow:
      0 25px 50px -12px rgba(0, 0, 0, 0.4),
      0 0 0 1px rgba(var(--va-background-border-rgb), 0.5);
    animation: dialog-enter 0.3s cubic-bezier(0.16, 1, 0.3, 1);
  }

  // Top progress bar
  &__top-progress {
    height: 4px;
    background: rgba(var(--va-background-border-rgb), 0.4);
    overflow: hidden;
  }

  &__top-progress-fill {
    height: 100%;
    background: linear-gradient(90deg, var(--va-primary) 0%, rgba(var(--va-primary-rgb), 0.7) 100%);
    transition: width 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  }

  // Header
  &__header {
    padding: 1.5rem 1.5rem 1rem;
    background: linear-gradient(
      180deg,
      rgba(var(--va-primary-rgb), 0.06) 0%,
      transparent 100%
    );
    border-bottom: 1px solid rgba(var(--va-background-border-rgb), 0.4);
  }

  &__header-main {
    display: flex;
    align-items: center;
    gap: 1rem;
  }

  &__icon {
    width: 48px;
    height: 48px;
    border-radius: 14px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(var(--va-primary-rgb), 0.1);
    color: var(--va-primary);
    flex-shrink: 0;

    &--urgent {
      background: rgba(var(--va-warning-rgb), 0.15);
      color: var(--va-warning);
      animation: pulse-icon 2s ease-in-out infinite;
    }
  }

  &__header-info {
    flex: 1;
    min-width: 0;
  }

  &__badge {
    display: inline-block;
    padding: 0.25rem 0.625rem;
    border-radius: 999px;
    font-size: 0.75rem;
    font-weight: 700;
    color: var(--va-primary);
    background: rgba(var(--va-primary-rgb), 0.1);
    margin-bottom: 0.375rem;
  }

  &__header-title {
    margin: 0;
    font-size: 1.125rem;
    font-weight: 700;
    color: var(--va-on-background-primary);
    line-height: 1.3;
  }

  // Body
  &__body {
    padding: 1.25rem 1.5rem;
    overflow-y: auto;
    flex: 1;

    &::-webkit-scrollbar {
      width: 6px;
    }

    &::-webkit-scrollbar-thumb {
      background: rgba(var(--va-background-border-rgb), 0.6);
      border-radius: 3px;
    }
  }

  &__meta {
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
    padding: 0.375rem 0.75rem;
    border-radius: 8px;
    font-size: 0.8125rem;
    color: var(--va-on-background-secondary);
    background: rgba(var(--va-background-border-rgb), 0.3);
    margin-bottom: 1rem;
  }

  &__title {
    margin: 0 0 1rem;
    font-size: 1.375rem;
    font-weight: 700;
    color: var(--va-on-background-primary);
    line-height: 1.4;
    letter-spacing: -0.01em;
  }

  &__content-card {
    border-radius: 12px;
    border: 1px solid rgba(var(--va-background-border-rgb), 0.5);
    background: var(--va-background-element);
    overflow: hidden;
  }

  &__content {
    max-height: min(35vh, 280px);
    overflow-y: auto;
    padding: 1.125rem 1.25rem;
    font-size: 0.9375rem;
    line-height: 1.7;
    color: var(--va-on-background-primary);

    &::-webkit-scrollbar {
      width: 5px;
    }

    &::-webkit-scrollbar-thumb {
      background: rgba(var(--va-primary-rgb), 0.2);
      border-radius: 4px;
    }

    p {
      margin: 0 0 0.75rem;

      &:last-child {
        margin-bottom: 0;
      }
    }
  }

  // Warning for important notifications
  &__warning {
    display: flex;
    align-items: flex-start;
    gap: 0.75rem;
    margin-top: 1rem;
    padding: 0.875rem 1rem;
    border-radius: 10px;
    background: rgba(var(--va-warning-rgb), 0.08);
    border: 1px solid rgba(var(--va-warning-rgb), 0.15);
  }

  &__warning-icon {
    flex-shrink: 0;
    color: var(--va-warning);
    margin-top: 0.125rem;
  }

  &__warning-text {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;

    strong {
      font-size: 0.875rem;
      font-weight: 600;
      color: var(--va-on-background-primary);
    }

    span {
      font-size: 0.8125rem;
      color: var(--va-on-background-secondary);
      line-height: 1.4;
    }
  }

  // Footer
  &__footer {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 0.75rem;
    padding: 1rem 1.5rem 1.5rem;
    border-top: 1px solid rgba(var(--va-background-border-rgb), 0.4);
    background: rgba(var(--va-background-border-rgb), 0.1);
  }

  &__action-btn {
    width: 100%;
    font-weight: 600;
    font-size: 0.9375rem;
  }

  &__helper {
    margin: 0;
    text-align: center;
    font-size: 0.8125rem;
    color: var(--va-on-background-secondary);
  }
}

// Animations
@keyframes dialog-enter {
  from {
    opacity: 0;
    transform: translateY(20px) scale(0.96);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

@keyframes pulse-icon {
  0%, 100% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.05);
    opacity: 0.9;
  }
}

.overlay-fade-enter-active,
.overlay-fade-leave-active {
  transition: opacity 0.3s ease;
}

.overlay-fade-enter-from,
.overlay-fade-leave-to {
  opacity: 0;
}
</style>
